// @flow

import React, { Component } from "react";
import { get, noop, isEqual } from "lodash";
import type {
  VideoSRC,
  RaceVideoFeedback,
  RaceStatusEnum
} from "@tvg/types/Race";
import type { NullaryFn, UnaryFn, BinaryFn } from "@tvg/types/Functional";
import horseRacingImage from "@fdr/static-ui/images/hr-video-placeholder.jpg";
import greyhoundsRacingImage from "@fdr/static-ui/images/gh-video-placeholder.jpg";

import buildButtonInfo from "../../_molecules/VideoButton/VideoButtonInfo";
import Video from "../../_organisms/Video";

import {
  VideoContainer,
  InlinePlayerContainer,
  PlayButton
} from "./styled-components";

type RaceInfoType = {
  mtp: number,
  raceNumber: string,
  trackAbbr: string,
  trackName: string,
  status: RaceStatusEnum,
  postTime: string,
  isGreyhound: boolean
};

type Props = {
  /**
   * Basic info for the track
   */
  raceInfo: RaceInfoType | null,
  /**
   * Displays video as an inline player with mask and play button
   */
  isVideoInline: boolean,
  /**
   * Starts the player on inline mode
   */
  isVideoInlinePlaying: boolean,
  /**
   * Displays the video in a fixed position on the container
   */
  isSticky: boolean,
  /**
   * Activate video auto play
   */
  isAutoPlay: boolean,
  /**
   * Video source data
   */
  videoSRC: VideoSRC,
  /**
   * Should start with replay
   */
  isReplay: boolean,
  /**
   * Video feedback data with the state of the video for the race
   */
  feedback: RaceVideoFeedback,
  /**
   * Function to be called when user presses close
   */
  onOpen: NullaryFn<void>,
  /**
   * Function to be called when user presses close
   */
  onClose: NullaryFn<void>,
  /**
   * Function to be called when video is un/muted (is called with true if video is being muted, false otherwise)
   */
  onVideoMute: UnaryFn<boolean, void>,
  /**
   * Function to be called when video is un/set to fullscreen (is called with true if video is being set to fullscreen, false otherwise)
   */
  onVideoFullScreen: BinaryFn<boolean, boolean, void>,
  /**
   * Function to be called when user presses play
   */
  onVideoPlay: NullaryFn<void>,
  /**
   * Function to be called when user presses pause
   */
  onVideoPause: NullaryFn<void>,
  /**
   * Callback when video stream is ready
   */
  videoStartCallback: NullaryFn<void>,
  /**
   * Function to be called when video is successfully loaded
   */
  onVideoSuccess: NullaryFn<void>,
  /**
   * Callback when video stream errors
   */
  videoErrorCallback: UnaryFn<?{ message: string, code: Number }, void>,
  /**
   * Function to be called when video throws an error (is called with the message displayed to the user)
   */
  onVideoError: UnaryFn<string, void>,
  /**
   * Flag that enables/disables videoFullscreenTilting
   */
  videoFullscreenTilt: boolean,
  /**
   * Reload streams on stream change
   */
  reloadStreams: NullaryFn<void>,
  /**
   * Callback to lock/unlock rotation on iOS devices to activate/deactivate rotation enabled fullscreen
   */
  deviceLockRotationToggle: UnaryFn<boolean, void>,
  /**
   * callback when switching video type
   */
  onSwitchVideoType: UnaryFn<boolean, void>,
  /**
   * callback when navigate on video
   */
  onNavigateVideo: UnaryFn<boolean, void>,
  /**
   * new stream video
   */
  hasNewStreamVideo: boolean,
  /**
   * is URP ctx
   */
  isURP?: boolean,
  /**
   * has a banner in the header related with T&Cs, Geolocation, etc
   */
  withFixedBanner?: boolean,
  /**
   * is FDTV context
   */
  isFDTV?: boolean
};

type State = {
  isVideoInlineAvailable: boolean
};

class RaceVideo extends Component<Props, State> {
  static defaultProps = {
    videoSRC: {
      sd: null,
      hd: null,
      replay: null
    },
    isReplay: false,
    isVideoInline: false,
    isVideoInlinePlaying: false,
    isSticky: true,
    isAutoPlay: false,
    raceInfo: null,
    onClose: noop,
    onOpen: noop,
    onVideoMute: noop,
    onVideoFullScreen: noop,
    onVideoPlay: noop,
    onVideoPause: noop,
    onVideoSuccess: noop,
    videoStartCallback: noop,
    videoErrorCallback: noop,
    onVideoError: noop,
    videoFullscreenTilt: false,
    reloadStreams: noop,
    deviceLockRotationToggle: noop,
    onSwitchVideoType: noop,
    onNavigateVideo: noop,
    hasNewStreamVideo: false,
    isURP: false,
    withFixedBanner: false,
    isFDTV: false
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      isVideoInlineAvailable: false
    };
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    if (
      props.isVideoInline &&
      (props.feedback.indexOf("AVAILABLE") === 0 ||
        !(props.feedback.indexOf("_NO_REPLAY") > -1))
    ) {
      return { ...state, isVideoInlineAvailable: true };
    }

    return null;
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      !isEqual(nextProps.videoSRC, this.props.videoSRC) ||
      nextProps.feedback !== this.props.feedback ||
      nextProps.isReplay !== this.props.isReplay ||
      nextProps.isVideoInlinePlaying !== this.props.isVideoInlinePlaying ||
      nextState.isVideoInlineAvailable !== this.state.isVideoInlineAvailable ||
      !isEqual(nextProps.raceInfo, this.props.raceInfo) ||
      !isEqual(nextProps.withFixedBanner, this.props.withFixedBanner) ||
      !isEqual(nextProps, this.props)
    );
  }

  renderPlayer = () => {
    const {
      videoSRC,
      isReplay,
      isVideoInline,
      isVideoInlinePlaying,
      raceInfo,
      videoStartCallback,
      videoErrorCallback,
      onVideoError,
      onVideoMute,
      onVideoFullScreen,
      onVideoPlay,
      onVideoPause,
      videoFullscreenTilt,
      onClose,
      onVideoSuccess
    } = this.props;

    const { isVideoInlineAvailable } = this.state;

    return (
      <Video
        raceId={`${get(raceInfo, "trackAbbr")}-${get(raceInfo, "raceNumber")}`}
        mtp={get(this.props, "raceInfo.mtp")}
        raceStatus={get(this.props, "raceInfo.status")}
        src={videoSRC}
        isReplay={isReplay}
        inlineVideo={
          isVideoInline && isVideoInlineAvailable && isVideoInlinePlaying
        }
        backGroundImage={
          get(raceInfo, "isGreyhound")
            ? greyhoundsRacingImage
            : horseRacingImage
        }
        onVideoMute={onVideoMute}
        onVideoFullScreen={onVideoFullScreen}
        onVideoPlay={onVideoPlay}
        onVideoPause={onVideoPause}
        startCallback={videoStartCallback}
        onVideoSuccess={onVideoSuccess}
        errorCallback={videoErrorCallback}
        onVideoError={onVideoError}
        closeHandler={onClose}
        videoFullscreenTilt={videoFullscreenTilt}
        deviceLockRotationToggle={this.props.deviceLockRotationToggle}
        reloadStreams={this.props.reloadStreams}
        onSwitchVideoType={this.props.onSwitchVideoType}
        onNavigateVideo={this.props.onNavigateVideo}
        hasNewStreamVideo={this.props.hasNewStreamVideo}
        isAutoPlay={this.props.isAutoPlay}
        isURP={this.props.isURP}
        isFDTV={this.props.isFDTV}
      />
    );
  };

  renderVideoInline = () => {
    const { feedback, onOpen, isVideoInlinePlaying, raceInfo, isAutoPlay } =
      this.props;
    const { isVideoInlineAvailable } = this.state;

    const playBtn = (
      <PlayButton
        data-qa-label="video-play-button"
        disabled={!isVideoInlineAvailable}
        onClick={() => isVideoInlineAvailable && onOpen()}
      >
        <span>
          {buildButtonInfo({
            feedback,
            vertical: true,
            iconSize: 32
          })}
        </span>
      </PlayButton>
    );

    return (
      <InlinePlayerContainer
        isPlaying={isVideoInlinePlaying}
        isGreyhound={get(raceInfo, "isGreyhound", false)}
        isVideoAvailable={isVideoInlineAvailable}
      >
        {isVideoInlinePlaying || isAutoPlay ? this.renderPlayer() : playBtn}
      </InlinePlayerContainer>
    );
  };

  render() {
    const { isVideoInline, isSticky, withFixedBanner, isFDTV } = this.props;
    const { isVideoInlineAvailable } = this.state;

    return (
      <VideoContainer
        isSticky={isSticky}
        isInline={isVideoInline}
        aspectRatio={isVideoInlineAvailable}
        withFixedBanner={withFixedBanner}
        isFDTV={isFDTV}
      >
        {isVideoInline ? this.renderVideoInline() : this.renderPlayer()}
      </VideoContainer>
    );
  }
}

export default RaceVideo;
